import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { createRental, getVehicleOccupiedSlots } from '../../api/services/rentalsService';
import Calendar from '../../components/Calendar';
import BookingConfirmation from '../../components/BookingConfirmation';
import FullScreenView from '../../components/FullScreenView';

interface BookingFormData {
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  totalHours: number;
  totalPrice: number;
}

const Booking = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [occupiedDates, setOccupiedDates] = useState<string[]>([]);
  const [isLoadingOccupiedDates, setIsLoadingOccupiedDates] = useState(false);
  
  // Récupérer les données du véhicule depuis la navigation
  const vehicle = location.state?.vehicle;
  const fromPage = location.state?.fromPage || 'explorer';
  
  const [formData, setFormData] = useState<BookingFormData>({
    startDate: '',
    endDate: '',
    startTime: '',
    endTime: '',
    totalHours: 0,
    totalPrice: 0
  });

  // Récupérer les jours occupés du véhicule
  useEffect(() => {
    const fetchOccupiedDates = async () => {
      if (!vehicle?.id_vehicle && !vehicle?.id && !vehicle?._id) {
        console.log('❌ Aucun ID de véhicule trouvé');
        return;
      }

      const vehicleId = vehicle.id_vehicle || vehicle.id || vehicle._id;
      console.log('🔍 Récupération des jours occupés pour le véhicule:', vehicleId);

      setIsLoadingOccupiedDates(true);
      try {
        // Calculer la période pour récupérer les jours occupés (mois en cours + 2 mois suivants)
        const now = new Date();
        const startDate = now.toISOString().split('T')[0];
        const endDate = new Date(now.getFullYear(), now.getMonth() + 2, 0).toISOString().split('T')[0];

        const response = await getVehicleOccupiedSlots(vehicleId, startDate, endDate, token || undefined);
        console.log('✅ Jours occupés récupérés:', response);

        if (response.success && response.data?.occupied_slots) {
          // Extraire les dates occupées des créneaux
          const dates = response.data.occupied_slots.map((slot: any) => {
            const startDate = new Date(slot.start_date);
            const endDate = new Date(slot.end_date);
            const dates: string[] = [];
            
            // Générer toutes les dates entre start_date et end_date
            const currentDate = new Date(startDate);
            while (currentDate <= endDate) {
              dates.push(currentDate.toISOString().split('T')[0]);
              currentDate.setDate(currentDate.getDate() + 1);
            }
            
            return dates;
          }).flat();

          // Supprimer les doublons
          const uniqueDates = Array.from(new Set(dates)) as string[];
          setOccupiedDates(uniqueDates);
          console.log('📅 Dates occupées:', uniqueDates);
        }
      } catch (error) {
        console.error('❌ Erreur lors de la récupération des jours occupés:', error);
        // En cas d'erreur, on continue sans bloquer l'interface
      } finally {
        setIsLoadingOccupiedDates(false);
      }
    };

    if (vehicle) {
      fetchOccupiedDates();
    }
  }, [vehicle, token]);

  // Calculer le prix total quand les dates/heures changent
  useEffect(() => {
    if (formData.startDate && formData.startTime && formData.endTime && vehicle?.hourly_rate) {
      // Pour un jour unique, utiliser la même date pour début et fin
      const effectiveEndDate = formData.endDate || formData.startDate;
      const startDateTime = new Date(`${formData.startDate}T${formData.startTime}`);
      const endDateTime = new Date(`${effectiveEndDate}T${formData.endTime}`);
      const diffInHours = (endDateTime.getTime() - startDateTime.getTime()) / (1000 * 60 * 60);
      
      if (diffInHours > 0) {
        const totalPrice = diffInHours * parseFloat(vehicle.hourly_rate);
        setFormData(prev => ({
          ...prev,
          totalHours: Math.ceil(diffInHours),
          totalPrice: totalPrice
        }));
      }
    }
  }, [formData.startDate, formData.endDate, formData.startTime, formData.endTime, vehicle]);

  // Rediriger si aucun véhicule n'est sélectionné
  useEffect(() => {
    if (!vehicle) {
      navigate('/overview');
    }
  }, [vehicle, navigate]);

  const handleInputChange = (field: keyof BookingFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Si on modifie les dates, vérifier si la plage devient valide
    if (field === 'startDate' || field === 'endDate') {
      const newFormData = { ...formData, [field]: value };
      
      // Si on a une plage complète, vérifier si elle est valide
      if (newFormData.startDate && newFormData.endDate) {
        if (!checkDateRangeOverlap(newFormData.startDate, newFormData.endDate)) {
          // La plage est maintenant valide, effacer le message d'erreur
          setMessage(null);
        }
      } else if (newFormData.startDate && !newFormData.endDate) {
        // Si on n'a qu'une date de début, effacer le message d'erreur
        setMessage(null);
      }
    }
  };

  // Callback pour les plages de dates invalides
  const handleInvalidRange = (message: string) => {
    setMessage({ text: message, type: 'error' });
  };

  // Fonction pour vérifier si une plage de dates chevauche des réservations
  const checkDateRangeOverlap = (startDate: string, endDate: string) => {
    if (!startDate || !endDate) return false;
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    // Vérifier si la plage sélectionnée chevauche des dates occupées
    return occupiedDates.some(occupiedDate => {
      const occupied = new Date(occupiedDate);
      return occupied >= start && occupied <= end;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    /*if (!token || !vehicle) {
      setMessage({ text: 'Erreur: Veuillez vous connecter et sélectionner un véhicule', type: 'error' });
      return;
    }*/

    // Validation des dates
    if (formData.startDate && formData.endDate) {
      const startDate = new Date(formData.startDate);
      const endDate = new Date(formData.endDate);
      
      if (endDate < startDate) {
        setMessage({ text: 'La date de fin doit être après la date de début', type: 'error' });
        return;
      }

      // Vérifier si la plage sélectionnée chevauche des réservations
      if (checkDateRangeOverlap(formData.startDate, formData.endDate)) {
        setMessage({ text: 'La période sélectionnée chevauche des réservations existantes. Veuillez choisir une autre période.', type: 'error' });
        return;
      }
    }

    // Validation des heures (pour jour unique ou plage)
    if (formData.startTime && formData.endTime) {
      const effectiveEndDate = formData.endDate || formData.startDate;
      const startDateTime = new Date(`${formData.startDate}T${formData.startTime}`);
      const endDateTime = new Date(`${effectiveEndDate}T${formData.endTime}`);
      
      if (endDateTime <= startDateTime) {
        setMessage({ text: 'L\'heure de fin doit être après l\'heure de début', type: 'error' });
        return;
      }
    }

    // Rediriger vers la page de paiement avec les détails de la réservation
    navigate('/payment', {
      state: {
        bookingDetails: {
          vehicle,
          startDate: formData.startDate,
          endDate: formData.endDate || formData.startDate,
          startTime: formData.startTime,
          endTime: formData.endTime,
          totalPrice: formData.totalPrice,
          totalHours: formData.totalHours
        }
      }
    });
  };

  if (!vehicle) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <FullScreenView
      isOpen={true}
      onClose={() => {
        // Retourner à la page d'origine
        if (fromPage === 'favorites') {
          navigate('/starred');
        } else {
          navigate('/overview');
        }
      }}
      title="Réserver votre véhicule"
      className="p-0"
    >
      <div className="w-full space-y-6 px-4 sm:px-6 py-6 overflow-y-auto flex-1">
        {/* En-tête avec avatar et informations principales */}
                     <div className="bg-gradient-to-r from-indigo-50 to-indigo-50 rounded-xl p-4 sm:p-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-6">
                             <div className="w-16 h-16 sm:w-20 sm:h-20 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
              <div className="text-3xl">{vehicle.icon || '🚗'}</div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-3 mb-2">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 truncate">
                  {vehicle.title}
                </h2>
                <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${
                  vehicle.status === 'disponible' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {vehicle.status}
                </span>
              </div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 text-gray-600">
                <div className="flex items-center space-x-2">
                  <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                  <span className="text-sm truncate">Type: {vehicle.type}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                  <span className="text-sm truncate">Prix: {vehicle.price}</span>
                </div>
              </div>
            </div>
            <div className="text-right flex-shrink-0">
                                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800">
                     <span className="w-2 h-2 bg-indigo-400 rounded-full mr-2"></span>
                     Disponible
                   </span>
            </div>
          </div>
        </div>

        {/* Contenu principal */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Détails du véhicule */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Détails du véhicule</h3>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="text-3xl">{vehicle.icon || '🚗'}</div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{vehicle.title}</h4>
                    <p className="text-sm text-gray-600">Type: {vehicle.type}</p>
                  </div>
                </div>

                <div className="border-t pt-4 space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Prix par heure:</span>
                                             <span className="font-semibold text-indigo-600">{vehicle.price}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Distance:</span>
                    <span className="text-gray-900">{vehicle.distance}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Statut:</span>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      vehicle.status === 'disponible' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {vehicle.status}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Formulaire de réservation */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Informations de réservation</h3>

              {message && (
                <div className={`mb-4 p-4 rounded-lg ${
                  message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {message.text}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Sélection des dates */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Sélectionnez votre plage de dates
                    </label>
                    {isLoadingOccupiedDates && (
                      <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                          <span className="text-sm text-blue-700">Chargement des disponibilités...</span>
                        </div>
                      </div>
                    )}
                    <div className="w-full">
                      <Calendar
                        startDate={formData.startDate}
                        endDate={formData.endDate}
                        onDateRangeSelect={(startDate, endDate) => {
                          // Si on a une plage complète, vérifier si elle est valide
                          if (startDate && endDate) {
                            if (checkDateRangeOverlap(startDate, endDate)) {
                              // Plage invalide - ne pas mettre à jour le formulaire, juste afficher l'erreur
                              setMessage({ text: 'La période sélectionnée chevauche des réservations existantes. Veuillez choisir une autre période.', type: 'error' });
                              return;
                            }
                          }
                          
                          // Plage valide ou sélection en cours - mettre à jour le formulaire
                          handleInputChange('startDate', startDate);
                          if (endDate === '') {
                            handleInputChange('endDate', '');
                          } else if (endDate) {
                            handleInputChange('endDate', endDate);
                          }
                        }}
                        minDate={new Date().toISOString().split('T')[0]}
                        occupiedDates={occupiedDates}
                        onInvalidRange={handleInvalidRange}
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Heure de début
                    </label>
                    <input
                      type="time"
                      value={formData.startTime}
                      onChange={(e) => handleInputChange('startTime', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Heure de fin
                    </label>
                    <input
                      type="time"
                      value={formData.endTime}
                      onChange={(e) => handleInputChange('endTime', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    />
                  </div>
                </div>

                

                                     {/* Résumé du prix */}
                     {(formData.startDate && formData.startTime && formData.endTime) && (
                       <div className="bg-gray-50 rounded-xl p-4">
                         <h3 className="font-semibold text-gray-900 mb-3">Résumé de la réservation</h3>

                         {/* Timeline de la réservation */}
                         <div className="mb-4">
                           <div className="flex items-center space-x-4">
                             <div className="flex flex-col items-center">
                               <div className="w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center">
                                 <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                 </svg>
                               </div>
                               <div className="text-xs text-gray-500 mt-1">Début</div>
                             </div>
                             <div className="flex-1 h-0.5 bg-gray-300"></div>
                             <div className="flex flex-col items-center">
                               <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center">
                                 <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                                 </svg>
                               </div>
                               <div className="text-xs text-gray-500 mt-1">Fin</div>
                             </div>
                           </div>

                           <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                             <div>
                               <div className="text-gray-500">Début</div>
                               <div className="font-medium">
                                 {formData.startDate && formData.startTime
                                   ? `${new Date(formData.startDate).toLocaleDateString('fr-FR')} à ${formData.startTime}`
                                   : 'Non défini'
                                 }
                               </div>
                             </div>
                             <div className="text-right">
                               <div className="text-gray-500">Fin</div>
                               <div className="font-medium">
                                 {formData.endDate && formData.endTime
                                   ? `${new Date(formData.endDate).toLocaleDateString('fr-FR')} à ${formData.endTime}`
                                   : formData.startDate && formData.endTime
                                   ? `${new Date(formData.startDate).toLocaleDateString('fr-FR')} à ${formData.endTime}`
                                   : 'Non défini'
                                 }
                               </div>
                             </div>
                           </div>
                         </div>

                         <div className="space-y-2">
                           <div className="flex justify-between">
                             <span className="text-gray-600">Type:</span>
                             <span className="font-medium">
                               {(!formData.endDate || formData.startDate === formData.endDate) ? 'Jour unique' : 'Plage de dates'}
                             </span>
                           </div>
                           <div className="flex justify-between">
                             <span className="text-gray-600">Durée:</span>
                             <span className="font-medium">{formData.totalHours} heure(s)</span>
                           </div>
                           <div className="flex justify-between">
                             <span className="text-gray-600">Prix total:</span>
                             <span className="font-semibold text-indigo-600">{formData.totalPrice.toFixed(2)}€</span>
                           </div>
                         </div>
                       </div>
                     )}

                                     {/* Bouton de réservation */}
                     <button
                       type="submit"
                       disabled={isLoading || !formData.startDate || !formData.startTime || !formData.endTime}
                       className={`w-full py-3 px-6 rounded-xl font-medium transition-colors ${
                         isLoading || !formData.startDate || !formData.startTime || !formData.endTime
                           ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                           : 'bg-indigo-600 text-white hover:bg-indigo-700'
                       }`}
                     >
                       {isLoading ? (
                         <div className="flex items-center justify-center">
                           <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                           Création de la réservation...
                         </div>
                       ) : (
                         'Procéder au paiement'
                       )}
                     </button>
              </form>
            </div>
          </div>
        </div>
      </div>
      
      {/* Modal de confirmation */}
      {showConfirmation && (
        <BookingConfirmation
          vehicle={vehicle}
          bookingDetails={{
            startDate: formData.startDate,
            endDate: formData.endDate,
            startTime: formData.startTime,
            endTime: formData.endTime,
            totalPrice: formData.totalPrice,
            totalHours: formData.totalHours
          }}
          onClose={() => {
            setShowConfirmation(false);
            // Retourner à la page d'origine
            if (fromPage === 'favorites') {
              navigate('/starred');
            } else {
              navigate('/overview');
            }
          }}
        />
      )}
    </FullScreenView>
  );
};

export default Booking;
